import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Checkbox, Col, Form, Input, InputNumber, Row, Space, Spin, Switch, Typography } from "antd";
import { ReloadOutlined, SaveOutlined, UndoOutlined } from "@ant-design/icons";
import InfoAyuda, { AyudaBloque } from "../../../Components/InfoAyuda";
import { botAsistenteApi } from "../../../services/botAsistente.api";
import type { NombreTareaIA, PruebaAsistenteTareas, TwilioAjustes, TwilioSettingsCambio } from "../../../interfaces/botAsistente.interfaces";
import {
  AVISO_TEXTO_GUARDADO,
  BorradorAjustes,
  BorradorTarea,
  LIMITE_MAX,
  LIMITE_MIN,
  MODELO_MAX,
  TEMPERATURA_MAX,
  TEMPERATURA_MIN,
  TIMEOUT_SEG_MAX,
  TIMEOUT_SEG_MIN,
  TOKENS_MAX,
  TOKENS_MIN,
  aBorrador,
  borradoresIguales,
  cambiosDelBorrador,
  erroresDelBorrador,
  fusionarBorrador,
  hayCambios,
} from "./botAsistente.utils";
import { MOTIVO_SOLO_SUPERADMIN } from "./EstadoModoSeccion";
import ProbarAsistente from "./ProbarAsistente";

const { Text } = Typography;

const TAREAS: readonly { clave: NombreTareaIA; titulo: string; accion: string; explicacion: string }[] = [
  {
    clave: "gasto.parse",
    titulo: "Leer un gasto nuevo",
    accion: "leer un gasto nuevo",
    explicacion: "El modelo que entiende el mensaje de WhatsApp y arma el gasto.",
  },
  {
    clave: "gasto.correct",
    titulo: "Corregir un gasto",
    accion: "corregir un gasto",
    explicacion: "El modelo que aplica lo que la persona corrige («no, eran 15 mil»).",
  },
];

/**
 * El borrador de «Restablecer a los valores por defecto». «Guardar el texto de los mensajes» NO es un valor de fábrica que un
 * administrador común pueda imponer: para él se conserva lo que dice el servidor (si el superadministrador lo encendió, el
 * restablecimiento no lo apaga, porque ese PUT sería rechazado entero con un 403 que no entendería). El superadministrador sí lo restablece.
 */
function restablecido(defaults: TwilioAjustes, ajustes: TwilioAjustes | null, esSuperadmin: boolean): BorradorAjustes {
  const base = aBorrador(defaults);
  return esSuperadmin || !ajustes ? base : { ...base, guardarTexto: ajustes.guardarTextoEventos };
}

export interface ModelosLimitesSeccionProps {
  /** El superadministrador es el único que puede encender «guardar el texto de los mensajes» (el servidor lo exige igual). */
  esSuperadmin: boolean;
  /** Lo guardado en el servidor. */
  ajustes: TwilioAjustes | null;
  /** Valores de fábrica, para «Restablecer». */
  defaults: TwilioAjustes;
  cargando: boolean;
  error: boolean;
  /** Vuelve a leer los ajustes del servidor («Actualizar» y «Reintentar»). */
  onReintentar: () => void;
  /** Después de guardar con éxito: el contenedor vuelve a leer los ajustes del servidor. */
  onGuardado: () => void | Promise<void>;
}

/**
 * (b) Modelos y límites. Un borrador local que se compara con lo guardado: «Guardar» manda SOLO las claves que cambiaron
 * y está apagado mientras no haya cambios o haya algo inválido. «Restablecer» llena el formulario con los valores de
 * fábrica pero no guarda hasta que se aprieta «Guardar» (igual que las otras pestañas de Configuración).
 *
 * Si llegan datos nuevos del servidor mientras la persona edita (la lectura de cada 45 s o «Actualizar»), NUNCA se le
 * pisa lo que lleva escrito: lo que ella tocó se conserva y el resto sigue al servidor (`fusionarBorrador`). Tampoco se
 * pierde el borrador si una lectura falla un momento: el formulario espera y, al volver el dato, se fusiona.
 * La clave de OpenAI NO se configura aquí: vive solo en las variables de entorno de Railway.
 */
const ModelosLimitesSeccion: React.FC<ModelosLimitesSeccionProps> = ({
  esSuperadmin,
  ajustes,
  defaults,
  cargando,
  error,
  onReintentar,
  onGuardado,
}) => {
  const [borrador, setBorrador] = useState<BorradorAjustes | null>(null);
  const [guardando, setGuardando] = useState(false);
  const enCurso = useRef(false);
  const baseRef = useRef<BorradorAjustes | null>(null);

  useEffect(() => {
    // Sin datos (una lectura falló) NO se borra el borrador ni la base: la pantalla muestra el error y, cuando el dato
    // vuelva, se fusiona con lo que la persona llevaba escrito.
    if (!ajustes) return;
    const baseAnterior = baseRef.current;
    const nuevaBase = aBorrador(ajustes);
    baseRef.current = nuevaBase;
    setBorrador((previo) => {
      if (previo === null || baseAnterior === null) return nuevaBase;
      const fusion = fusionarBorrador(previo, baseAnterior, nuevaBase);
      return borradoresIguales(fusion, previo) ? previo : fusion;
    });
  }, [ajustes]);

  const cambiarTarea = (clave: NombreTareaIA, parcial: Partial<BorradorTarea>) =>
    setBorrador((b) => (b ? { ...b, tareas: { ...b.tareas, [clave]: { ...b.tareas[clave], ...parcial } } } : b));
  const cambiar = (parcial: Partial<BorradorAjustes>) => setBorrador((b) => (b ? { ...b, ...parcial } : b));

  const errores = borrador ? erroresDelBorrador(borrador) : {};
  const errorDe = (campo: string): string | undefined => (errores as Record<string, string | undefined>)[campo];
  const cambio: TwilioSettingsCambio = borrador && ajustes ? cambiosDelBorrador(borrador, ajustes) : {};
  // «Guardar el texto de los mensajes» es SOLO del superadministrador (el servidor lo exige y rechaza TODO el PUT si llega esa clave
  // desde otro rol). Un administrador común nunca debe mandarla, ni siquiera por un borrador que quedó distinto (por ejemplo tras
  // «Restablecer»): se saca del diff, así «Guardar» ni se habilita ni manda por un cambio que no podría hacer.
  if (!esSuperadmin) delete cambio.guardarTextoEventos;
  const hay = hayCambios(cambio);
  const invalido = Object.keys(errores).length > 0;

  const guardar = async () => {
    if (!ajustes || !borrador || !hay || invalido || enCurso.current) return;
    enCurso.current = true;
    setGuardando(true);
    try {
      const ok = await botAsistenteApi.updateTwilioSettings(cambio);
      if (ok) await onGuardado();
    } finally {
      enCurso.current = false;
      setGuardando(false);
    }
  };

  const cuerpo = () => {
    if ((cargando && ajustes === null && !error) || (ajustes !== null && !error && borrador === null)) {
      return (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      );
    }
    if (error || !ajustes || !borrador) {
      return (
        <Alert
          type="error"
          showIcon
          data-testid="modelos-error"
          message="No se pudieron cargar los ajustes del asistente"
          description="Para no pisar los valores guardados con otros distintos, el formulario no se muestra hasta que la carga resulte bien."
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={() => onReintentar()}>
              Reintentar
            </Button>
          }
        />
      );
    }
    const sinTope = borrador.sinTopeTokens;
    // Lo que hay ESCRITO ahora en las dos tareas (guardado o no): es lo que prueba «Probar el asistente». `null` = no enviar la temperatura.
    const escritas: PruebaAsistenteTareas = {};
    for (const t of TAREAS) {
      const b = borrador.tareas[t.clave];
      escritas[t.clave] = { model: b.model.trim(), temperature: b.sinTemperatura ? null : b.temperature };
    }
    return (
      <Form layout="vertical" disabled={guardando} onFinish={() => void guardar()} data-testid="modelos-form">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="La clave de OpenAI no se configura aquí"
          description="Por seguridad vive solo en las variables de entorno de Railway: esta pantalla no la pide ni la muestra."
        />

        {TAREAS.map((t) => {
          const b = borrador.tareas[t.clave];
          return (
            <Card key={t.clave} size="small" type="inner" title={t.titulo} style={{ marginBottom: 12 }} data-testid={`tarea-${t.clave}`}>
              <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                {t.explicacion}
              </Text>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Modelo"
                    validateStatus={errorDe(`${t.clave}.model`) ? "error" : undefined}
                    help={errorDe(`${t.clave}.model`)}
                  >
                    <Input
                      value={b.model}
                      maxLength={MODELO_MAX}
                      onChange={(e) => cambiarTarea(t.clave, { model: e.target.value })}
                      placeholder="gpt-4.1-mini"
                      autoComplete="off"
                      spellCheck={false}
                      aria-label={`Modelo para ${t.accion}`}
                      data-testid={`modelo-${t.clave}`}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Temperatura"
                    validateStatus={errorDe(`${t.clave}.temperature`) ? "error" : undefined}
                    help={errorDe(`${t.clave}.temperature`)}
                    extra="0 = respuestas siempre iguales; más alto = más variadas."
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={TEMPERATURA_MIN}
                      max={TEMPERATURA_MAX}
                      step={0.1}
                      precision={2}
                      value={b.temperature}
                      disabled={b.sinTemperatura}
                      onChange={(v) => cambiarTarea(t.clave, { temperature: typeof v === "number" ? v : null })}
                      aria-label={`Temperatura para ${t.accion}`}
                      data-testid={`temperatura-${t.clave}`}
                    />
                    <Checkbox
                      style={{ marginTop: 6 }}
                      checked={b.sinTemperatura}
                      onChange={(e) => cambiarTarea(t.clave, { sinTemperatura: e.target.checked })}
                      data-testid={`sin-temperatura-${t.clave}`}
                    >
                      No enviar temperatura
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          );
        })}

        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Algunos modelos nuevos no aceptan la temperatura"
          description="Si el modelo que elijas la rechaza o la ignora, marca «No enviar temperatura»."
        />

        {/* Prueba los modelos ESCRITOS arriba, guardados o no. No se ejecuta sola ni guarda nada. */}
        <ProbarAsistente tareas={escritas} bloqueado={invalido} guardando={guardando} />

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Tiempo máximo de espera"
              validateStatus={errores.timeoutSeg ? "error" : undefined}
              help={errores.timeoutSeg}
              extra="Cuánto espera al modelo antes de rendirse."
            >
              <InputNumber
                style={{ width: "100%" }}
                min={TIMEOUT_SEG_MIN}
                max={TIMEOUT_SEG_MAX}
                step={1}
                precision={0}
                value={borrador.timeoutSeg}
                onChange={(v) => cambiar({ timeoutSeg: typeof v === "number" ? v : null })}
                addonAfter="segundos"
                aria-label="Tiempo máximo de espera al modelo, en segundos"
                data-testid="timeout-seg"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Máximo de tokens de respuesta"
              validateStatus={errores.maxTokens ? "error" : undefined}
              help={errores.maxTokens}
              extra="Largo máximo de lo que escribe el modelo."
            >
              {/* Con «Sin tope» el campo queda vacío y dice «Sin tope»: el número que se guardó por dentro (1000) no está en vigor y no debe verse. */}
              <InputNumber
                style={{ width: "100%" }}
                min={TOKENS_MIN}
                max={TOKENS_MAX}
                step={100}
                precision={0}
                value={sinTope ? null : borrador.maxTokens}
                placeholder={sinTope ? "Sin tope" : undefined}
                disabled={sinTope}
                onChange={(v) => cambiar({ maxTokens: typeof v === "number" ? v : null })}
                aria-label="Máximo de tokens de respuesta"
                data-testid="max-tokens"
              />
              <Checkbox
                style={{ marginTop: 6 }}
                checked={sinTope}
                onChange={(e) => cambiar({ sinTopeTokens: e.target.checked })}
                data-testid="sin-tope-tokens"
              >
                Sin tope
              </Checkbox>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Límite de mensajes por minuto"
              validateStatus={errores.limite ? "error" : undefined}
              help={errores.limite}
              extra="Por cada número de teléfono. Al pasarse, el asistente pide esperar."
            >
              <InputNumber
                style={{ width: "100%" }}
                min={LIMITE_MIN}
                max={LIMITE_MAX}
                step={1}
                precision={0}
                value={borrador.limite}
                onChange={(v) => cambiar({ limite: typeof v === "number" ? v : null })}
                addonAfter="por minuto"
                aria-label="Límite de mensajes por minuto, por número de teléfono"
                data-testid="limite-minuto"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Guardar el texto de los mensajes en la actividad">
          <Space align="center" wrap>
            <Switch
              checked={borrador.guardarTexto}
              disabled={!esSuperadmin}
              onChange={(v) => cambiar({ guardarTexto: v })}
              aria-label="Guardar el texto de los mensajes"
              data-testid="guardar-texto"
            />
            <Text type="secondary">{borrador.guardarTexto ? "Se guarda el texto" : "No se guarda (lo normal)"}</Text>
            {!esSuperadmin && (
              <Text type="secondary" data-testid="guardar-texto-solo-superadmin">
                {MOTIVO_SOLO_SUPERADMIN}
              </Text>
            )}
          </Space>
          {/* UNA sola advertencia, coherente con lo que dice el SERVIDOR (a 360 px se apilaban dos casi iguales, y la segunda hablaba en
              condicional estando ya activo): si el texto SE ESTÁ guardando, la de «se está guardando» (se ve siempre, haya o no cambios
              sin guardar); si no, la explicación de qué pasa si se activa (amarilla mientras el interruptor esté encendido sin guardar). */}
          {ajustes.guardarTextoEventos ? (
            <Alert type="warning" showIcon style={{ marginTop: 8 }} data-testid="aviso-texto-guardado" message={AVISO_TEXTO_GUARDADO} />
          ) : (
            <Alert
              type={borrador.guardarTexto ? "warning" : "info"}
              showIcon
              style={{ marginTop: 8 }}
              data-testid="aviso-privacidad"
              message="Privacidad"
              description="Si lo activas, lo que la gente escribe por WhatsApp queda guardado tal cual en la actividad, y puede incluir datos personales. Solo lo ve el superadministrador. Actívalo únicamente para buscar un problema y apágalo después."
            />
          )}
        </Form.Item>

        <Space wrap style={{ maxWidth: "100%" }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            htmlType="submit"
            loading={guardando}
            disabled={!hay || invalido}
            data-testid="modelos-guardar"
          >
            Guardar cambios
          </Button>
          <Button icon={<UndoOutlined />} disabled={!hay || guardando} onClick={() => baseRef.current && setBorrador(baseRef.current)} data-testid="modelos-deshacer">
            Deshacer cambios
          </Button>
          {/* Texto largo: sin esto no parte la línea y a 320 px de pantalla desbordaba la página 8 px. */}
          <Button
            disabled={guardando}
            onClick={() => setBorrador(restablecido(defaults, ajustes, esSuperadmin))}
            style={{ whiteSpace: "normal", height: "auto", minHeight: 32, maxWidth: "100%", wordBreak: "break-word" }}
            data-testid="modelos-restablecer"
          >
            Restablecer a los valores por defecto
          </Button>
        </Space>
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }} data-testid="modelos-estado">
            {invalido
              ? "Hay campos con errores: corrígelos para poder guardar."
              : hay
                ? "Hay cambios sin guardar."
                : "Sin cambios."}
          </Text>
        </div>
      </Form>
    );
  };

  return (
    <Card
      size="small"
      data-testid="asistente-seccion-modelos"
      title={
        <Space size={4}>
          <span>Modelos y límites</span>
          <InfoAyuda titulo="Modelos y límites" testId="ayuda-modelos">
            <AyudaBloque titulo="Modelo y temperatura">
              El asistente usa un modelo de inteligencia artificial de OpenAI para entender los mensajes. Aquí eliges cuál usa
              en cada tarea. La temperatura controla cuánto varía su respuesta: 0 es lo más estable.
            </AyudaBloque>
            <AyudaBloque titulo="Probar antes de guardar">
              El botón «Probar el asistente» comprueba, sin guardar nada, que los modelos escritos existan y sirvan para leer y corregir un
              gasto, y que el servicio del bot tenga lo que necesita. Úsalo antes de guardar un cambio de modelo.
            </AyudaBloque>
            <AyudaBloque titulo="Tiempo, tokens y límite">
              El tiempo máximo y el tope de tokens evitan respuestas eternas o demasiado largas. El límite por minuto frena a
              quien escriba demasiado rápido.
            </AyudaBloque>
            <AyudaBloque titulo="Cuándo rige un cambio">
              Los valores guardados llegan al asistente en unos minutos, sin reiniciar nada.
            </AyudaBloque>
            <AyudaBloque titulo="Guardar el texto de los mensajes">
              Solo el superadministrador puede encenderlo. Guarda lo que la gente escribe, y puede incluir datos personales:
              úsalo únicamente para buscar un problema y apágalo después.
            </AyudaBloque>
          </InfoAyuda>
        </Space>
      }
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={() => onReintentar()} data-testid="modelos-actualizar">
          Actualizar
        </Button>
      }
    >
      {cuerpo()}
    </Card>
  );
};

export default ModelosLimitesSeccion;
